import * as React from 'react';
import { useIntl } from 'react-intl';
import Chart from 'chart.js';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ChartComponent from '../../Chart';
import LeftAxis from './LeftAxix';
import {
  GRAPH_BORDER_COLOR,
  GRAPH_COMPR_BACKGROUND_COLOR,
  GRAPH_COMPR_BORDER_COLOR,
  GRAPH_POINT_BORDER_COLOR,
  GRAPH_SELECTED_POINT_COLOR,
  IGraphCode,
  IGraphData,
  getClickedPointForCompressedGraph,
  getMinutesGraphHeight,
  plugin,
} from '../GraphService';
import {
  renderEcoDriveTooltip,
  getClickedPointAndViolations,
  getClickedPointColorAndViolations,
  IEcoDriveXYData,
  IEcoDriveDataset,
} from './EcoDriveService';
import { lastClickedTimestampAtom, tooltipCodeAtom } from '../GraphState';
import { isMenuShownAtom } from '../../MainMenu/MainMenuState';

const RED_TYPE = '#b50536';
const GREEN_TYPE = '#1C957A';
const ORANGE_TYPE = '#df731d';

const RED_BACKGROUND = 'rgb(181, 5, 54, 0.5)';
const GREEN_BACKGROUND = 'rgba(5, 181, 144, 0.5)';
const ORANGE_BACKGROUND = 'rgb(233, 115, 29, 0.5)';

interface IProps {
  graphData: IGraphData;
  noAnimation?: string;
  isCompressed: boolean;
  isOnline?: boolean;
  isDetail: boolean;
  isSelectable?: boolean;
}

export default function EcoDriveChart(props: IProps) {
  const { formatMessage: f } = useIntl();
  const setTooltipCode = useSetRecoilState<IGraphCode | null>(tooltipCodeAtom);
  const lastClickedTimestamp = useRecoilValue<string>(lastClickedTimestampAtom);
  const mainMenuToggle = useRecoilValue<boolean>(isMenuShownAtom);
  const [graphWidthDiff, setGraphWidthDiff] = React.useState<number>(-15);
  const [graphWidthAfterResize, setGraphWidthAfterResize] = React.useState<null | number>(null);

  if (Chart && Chart.defaults && Chart.defaults.global && Chart.defaults.global.plugins) {
    Chart.defaults.global.plugins.gridBgColor = {
      backgroundColorRepeat: false,
      backgroundColor: 'rgba(127,127,127,0.0)',
    };
  }

  Chart.plugins.register(plugin);

  const handleGraphInitialized = (event) => {
    if (event.target.id === 'toggleMenu') {
      setGraphWidthDiff(!mainMenuToggle ? 163 : -163);
      if (graphWidthAfterResize) {
        setGraphWidthAfterResize(null);
      }
    }
  };

  const handleGraphResize = () => {
    const canvas = document.getElementById('componentChart');
    if (canvas) {
      setGraphWidthAfterResize(canvas.clientWidth);
    }
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleGraphResize);
    window.addEventListener('click', handleGraphInitialized);
    return () => {
      window.removeEventListener('resize', handleGraphResize);
      window.addEventListener('click', handleGraphInitialized);
    };
  });

  const getColorString = (value?: number | null) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (value >= 8) {
      return GREEN_TYPE;
    }
    if (value >= 5) {
      return ORANGE_TYPE;
    }
    return RED_TYPE;
  };

  const handleWheelOutside = React.useCallback(() => {
    const tooltipEl = document.getElementById('chartjs-tooltip');
    if (tooltipEl) {
      tooltipEl.style.opacity = '0';
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('wheel', handleWheelOutside, false);
    return () => {
      document.addEventListener('wheel', handleWheelOutside, false);
    };
  }, [handleWheelOutside]);

  React.useEffect(() => {
    document.addEventListener('wheel', handleWheelOutside, false);
    return () => {
      document.addEventListener('wheel', handleWheelOutside, false);
    };
  }, [handleWheelOutside]);

  const getBackgroundColor = (type: string) => {
    if (type === GREEN_TYPE) {
      return GREEN_BACKGROUND;
    }
    if (type === ORANGE_TYPE) {
      return ORANGE_BACKGROUND;
    }
    return RED_BACKGROUND;
  };

  const generateDatasets = React.useCallback(() => {
    if (props.graphData && props.graphData.yLabels && props.graphData.yLabels.length > 0) {
      const xData = props.graphData.xLabels;
      const yData = props.graphData.yLabels;
      const datasets: IEcoDriveDataset[] = [];
      let xyData: IEcoDriveXYData[] = [];

      Object.keys(yData).forEach((key) => {
        const index = Number(key);
        xyData.push({ x: xData[index], y: yData[index] });
        const currentColorType = getColorString(yData[index]);
        const nextColorType = getColorString(yData[index + 1]);

        if (currentColorType && currentColorType !== nextColorType) {
          const datasetsData =
            index + 1 < yData.length
              ? [...xyData, { x: xData[index + 1], y: yData[index + 1] }]
              : xyData;
          const clickedPoint = getClickedPointAndViolations(
            datasetsData,
            lastClickedTimestamp,
            props.graphData.tooltips
          );
          datasets.push({
            data: datasetsData,
            backgroundColor: getBackgroundColor(currentColorType),
            borderColor: currentColorType,
            borderWidth: 2,
            pointBackgroundColor: getClickedPointColorAndViolations(
              datasetsData,
              lastClickedTimestamp,
              props.graphData.tooltips
            ),
            pointBorderColor: GRAPH_POINT_BORDER_COLOR,
            cubicInterpolationMode: 'monotone',
            pointRadius: clickedPoint,
            pointHoverRadius: clickedPoint,
          });
          xyData = [];
        }
      });
      return datasets;
    }
    return [];
  }, [lastClickedTimestamp, props.graphData]);

  const getDatasets = React.useCallback(() => {
    return props.isCompressed
      ? [
          {
            label: '',
            // lineTension: 0,
            data: props.graphData.yLabels,
            backgroundColor: '',
            borderColor: GRAPH_BORDER_COLOR,
            borderWidth: 2,
            pointBackgroundColor: GRAPH_SELECTED_POINT_COLOR,
            pointBorderColor: GRAPH_POINT_BORDER_COLOR,
            pointRadius: getClickedPointForCompressedGraph(
              lastClickedTimestamp,
              props.graphData.xLabels
            ),
            pointHoverRadius: getClickedPointForCompressedGraph(
              lastClickedTimestamp,
              props.graphData.xLabels
            ),
            cubicInterpolationMode: 'monotone',
            fill: 0,
          },
          {
            label: '',
            // lineTension: 0,
            data: props.graphData.minLabels,
            backgroundColor: GRAPH_COMPR_BACKGROUND_COLOR,
            borderColor: GRAPH_COMPR_BORDER_COLOR,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            cubicInterpolationMode: 'monotone',
            fill: 0,
          },
          {
            label: '',
            // lineTension: 0,
            data: props.graphData.maxLabels,
            backgroundColor: GRAPH_COMPR_BACKGROUND_COLOR,
            borderColor: GRAPH_COMPR_BORDER_COLOR,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            cubicInterpolationMode: 'monotone',
            fill: 0,
          },
        ]
      : generateDatasets();
  }, [generateDatasets, lastClickedTimestamp, props.graphData, props.isCompressed]);

  const graphOptions = {
    // title: {
    //   display: true,
    //   // text: 'Graph',
    // },
    // responsive: false,
    horizontalLine: [
      {
        y: 5,
        style: '#df731d',
        // text: 'max',
      },
      {
        y: 8,
        style: '#1C957A',
      },
      {
        y: 10.49,
        style: '#d9d9d9',
      },
    ],
    legend: {
      display: false,
      labels: {
        fontColor: '#555',
      },
    },
    tooltips: {
      enabled: false,
      intersect: false,

      custom(tooltipModel) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const code = renderEcoDriveTooltip(
          tooltipModel,
          this,
          props.graphData.tooltips,
          f,
          props.isCompressed
        );
        setTooltipCode(code);
      },
    },
    // plugins: {
    //   zoom: {
    //     // Container for pan options
    //     pan: {
    //       // Boolean to enable panning
    //       enabled: true,
    //       // Panning directions. Remove the appropriate direction to disable
    //       // Eg. 'y' would only allow panning in the y direction
    //       mode: 'x',
    //     },

    //     // Container for zoom options
    //     zoom: {
    //       // Boolean to enable zooming
    //       enabled: true,
    //       // Zooming directions. Remove the appropriate direction to disable
    //       // Eg. 'y' would only allow zooming in the y direction
    //       mode: 'x',
    //     },
    //   },
    // },
    maintainAspectRatio: false,
    aspectRatio: 4,
    scales: {
      yAxes: [
        {
          display: true,
          ticks: {
            beginAtZero: true,
            fontColor: 'transparent',
            steps: 5,
            stepValue: 2,
            stepSize: 2,
            max: 10.5,
            // fontSize: 14,
            // reverse: false,
          },
          afterTickToLabelConversion(scaleInstance) {
            if (scaleInstance && scaleInstance.ticks && scaleInstance.ticks[0]) {
              scaleInstance.ticks[0] = null;
            }
            // scaleInstance.ticks[scaleInstance.ticks.length - 1] = null;
            // scaleInstance.ticksAsNumbers[0] = null;
            // scaleInstance.ticksAsNumbers[scaleInstance.ticksAsNumbers.length - 1] = null;
          },
          gridLines: {
            // zeroLineColor: 'green',
            // color: '#ccc',
            // backgroundColor: ['lightgrey', 'white'],
            // backgroundColorRepeat: true,
          },
        },
      ],
      xAxes: [
        {
          display: true,
          // display: false,
          type: 'time',
          time: {
            unit: 'minute',
            // max: '2020-10-09T19:10:00Z',
            // min: '2020-10-09T18:24:00Z',
            unitStepSize: 1,
            displayFormats: {
              second: 'HH:mm:ss',
              minute: 'HH:mm',
              hour: 'HH',
            },
          },
          // stacked: false,
          // scaleLabel: {
          //   display: true,
          //   labelString: 'X axe name',
          //   fontColor: '#b50536',
          //   fontSize: 10,
          // },
          // type: 'time',
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            maxRotation: 0,
            minRotation: 0,
          },
          // ticks: {
          //   // fontColor: 'green',
          //   // fontSize: 14,
          // },
          gridLines: {
            // zeroLineColor: 'green',
            color: '#e6e6e6',
            circular: true,
            backgroundColor: ['#f2f2f2', '#fff'],
            backgroundColorRepeat: true,
          },
        },
      ],
    },
    [props.noAnimation || 'undefined']: false,
  };

  return (
    <div style={{ position: 'relative' }}>
      <LeftAxis isDetail={!!props.isDetail} />
      {props.graphData.yLabels && props.graphData.yLabels.length > 0 ? (
        [
          <ChartComponent
            data={{
              // labels: props.isCompressed || subpageParams ? props.graphData.xLabels : undefined,
              labels: props.graphData.xLabels,
              datasets: getDatasets(),
            }}
            options={graphOptions}
            // type="multicolorLine"
            isOnline={props.isOnline}
            isSelectable={props.isSelectable}
            height={getMinutesGraphHeight(props.graphData, !!props.isDetail)}
          />,
          <div
            style={{
              position: 'absolute',
              top: '0px',
              right: '0px',
              width: '1px',
              height: props.isDetail ? '378px' : '170px',
              background: '#d9d9d9',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: '0px',
                right: '0px',
                width: '30px',
                height: '1px',
                background: '#a6a6a6',
              }}
            />
          </div>,
        ]
      ) : (
        <div style={{ height: props.isDetail ? '408px' : '200px' }} />
      )}
    </div>
  );
}
